import {AbstractControl, FormArray, FormControl, ValidationErrors} from '@angular/forms';
import {
  calculateAgeTruncated,
  HUMANIA_EMMA_V2_ELIGIBILITY_MAX_AGE,
  NON_ALPHANUMERIC_REGEX,
} from '@emma-labs/shared/typescript';
import {isEmail, isPhoneNumber, isPostalCode} from 'class-validator';
import dayjs from 'dayjs';
import {stringToDate} from './utility-functions';

const CONTAINS_UPPERCASE_REGEX = new RegExp('(?=.*[A-Z])');
const CONTAINS_LOWERCASE_REGEX = new RegExp('(?=.*[a-z])');
const CONTAINS_NUMBER_REGEX = new RegExp('(?=.*[0-9])');
const MIN_LENGTH_8_REGEX = new RegExp('(?=.{8,})');
const NUMBER_REGEX = /^[0-9]*$/;
const LAST_NAME_REGEX = /#\d+$/;
const SPECIAL_CHARACTER_REGEX = /[!@#$%?&*()_+=/\\"<>;:~¨^`«»{},.¬°]/;
export const validators = {
  notEmpty: (input: AbstractControl): ValidationErrors | null => {
    return (input.value != null || input.value === 0) && input.value.toString().trim() !== ''
      ? null
      : {notEmpty: true};
  },

  dateInFuture: (input: AbstractControl): ValidationErrors | null => {
    const birthday = dayjs(input.value, 'D/M/YYYY');
    const now = dayjs();

    if (!isEmpty(input.value) && isDateInputValid(input)) {
      return birthday > now ? {dateInFutureInvalid: true} : null;
    } else {
      return null;
    }
  },

  ageMaxEmmaV2Product: (input: AbstractControl): ValidationErrors | null => {
    const age = calculateAgeTruncated(stringToDate(input.value));

    return age <= HUMANIA_EMMA_V2_ELIGIBILITY_MAX_AGE || isEmpty(input.value)
      ? null
      : {ageMaxEmmaV2ProductInvalid: true};
  },

  noSpecialCharacter: (input: AbstractControl): ValidationErrors | null =>
    !SPECIAL_CHARACTER_REGEX.test(input.value) || isEmpty(input.value)
      ? null
      : {noSpecialCharacterInvalid: true},

  alphanumeric: (input: AbstractControl): ValidationErrors | null =>
    NON_ALPHANUMERIC_REGEX.test(input.value) ? {alphanumericInvalid: true} : null,

  noNumber: (input: AbstractControl): ValidationErrors | null =>
    !CONTAINS_NUMBER_REGEX.test(input.value) || isEmpty(input.value) ? null : {noNumberInvalid: true},

  weight: (input: AbstractControl): ValidationErrors | null =>
    NUMBER_REGEX.test(input.value) || isEmpty(input.value) ? null : {weightInvalid: true},

  atLeastOneChecked: (control: AbstractControl): ValidationErrors | null =>
    Object.values(control.value).every((value) => value !== true) ? {atLeastOneCheckedInvalid: true} : null,

  password: (input: AbstractControl): ValidationErrors | null => {
    const containsUpperCase = CONTAINS_UPPERCASE_REGEX.test(input.value);
    const containsLowerCase = CONTAINS_LOWERCASE_REGEX.test(input.value);
    const containsNumber = CONTAINS_NUMBER_REGEX.test(input.value);
    const minLength8 = MIN_LENGTH_8_REGEX.test(input.value);

    return (containsUpperCase && containsLowerCase && containsNumber && minLength8) || isEmpty(input.value)
      ? null
      : {
          containsUpperCaseInvalid: !containsUpperCase,
          containsLowerCaseInvalid: !containsLowerCase,
          containsNumberInvalid: !containsNumber,
          minLength8Invalid: !minLength8,
        };
  },

  passwordConfirmation: (formGroupInput: AbstractControl): ValidationErrors | null => {
    const newPassword = formGroupInput.get('newPassword')?.value;
    const confirmNewPassword = formGroupInput.get('confirmNewPassword')?.value;

    return newPassword === confirmNewPassword ? null : {passwordConfirmationInvalid: true};
  },

  totalShareHundred: (form: AbstractControl): ValidationErrors | null => {
    if (form instanceof FormArray) {
      let totalShare = 0;
      let isOneControlZero = false;

      form.controls.forEach((formGroup: AbstractControl) => {
        const currShare = parseInt(formGroup.get('share')?.value);
        if (currShare === 0 || isNaN(currShare)) {
          isOneControlZero = true;
        }
        totalShare += currShare;
      });
      return !isOneControlZero && totalShare === 100 ? null : {totalShareHundredInvalid: true};
    } else {
      throw new Error('must be a formArray');
    }
  },

  phone: (input: AbstractControl): ValidationErrors | null =>
    isPhoneNumber(input.value, 'CA') || isEmpty(input.value) ? null : {phoneInvalid: true},

  isPositive: (input: AbstractControl): ValidationErrors | null =>
    !input.value || parseInt(input.value) <= 0 ? {isPositiveInvalid: true} : null,

  atLeast19YearsOld: (input: AbstractControl): ValidationErrors | null => {
    const birthday = dayjs(input.value, 'D/M/YYYY');

    if (!isDateInputValid(input) || isEmpty(input.value)) {
      return null;
    }

    if (calculateAgeTruncated(birthday.toDate()) < 19 && birthday <= dayjs()) {
      return {atLeast18YearsOldInvalid: true};
    } else {
      return null;
    }
  },

  atLeast18YearsOld: (input: AbstractControl): ValidationErrors | null => {
    const birthday = dayjs(input.value, 'D/M/YYYY');

    if (!isDateInputValid(input) || isEmpty(input.value)) {
      return null;
    }

    if (calculateAgeTruncated(birthday.toDate()) < 18 && birthday <= dayjs()) {
      return {atLeast18YearsOldInvalid: true};
    } else {
      return null;
    }
  },

  date: (input: AbstractControl): ValidationErrors | null => {
    const formattedDate = dayjs(input.value, 'D/M/YYYY');

    if (isEmpty(input.value)) {
      return null;
    }

    if (isDateInputValid(input)) {
      return formattedDate.isValid() ? null : {dateInvalid: true};
    } else {
      return {dateInvalid: true};
    }
  },

  firstName: (input: AbstractControl): ValidationErrors | null =>
    input?.value.toLowerCase() !== 'visitor' || isEmpty(input.value) ? null : {firstNameInvalid: true},

  lastName: (input: AbstractControl): ValidationErrors | null =>
    !LAST_NAME_REGEX.test(input?.value) || isEmpty(input.value) ? null : {lastNameInvalid: true},

  email: (input: AbstractControl): ValidationErrors | null =>
    isEmail(input.value) || isEmpty(input.value) ? null : {emailInvalid: true},

  language: (input: AbstractControl): ValidationErrors | null =>
    input.value === 'Unknown' ? {languageInvalid: true} : null,

  postalCode: (input: AbstractControl): ValidationErrors | null =>
    isPostalCode(input.value, 'CA') || isEmpty(input.value) ? null : {postalCodeInvalid: true},

  numeric: (input: AbstractControl): ValidationErrors | null =>
    NUMBER_REGEX.test(input.value) || isEmpty(input.value) ? null : {numericInvalid: true},

  isTrue: (input: AbstractControl): ValidationErrors | null =>
    input.value === true ? null : {isTrueInvalid: true},

  percentage: (input: AbstractControl): ValidationErrors | null =>
    parseInt(input.value) <= 100 && parseInt(input.value) >= 0 ? null : {percentageInvalid: true},
};

function isEmpty(input: FormControl<string>): boolean {
  return !input || input.value === '' || input.value === null;
}

function isDateInputValid(input: AbstractControl): boolean {
  const values = input?.value?.split('/');

  return values && !(values[0] > 31 || values[1] > 12 || values[2] < 1900) && values[0] > 0 && values[1] > 0;
}
